import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Check,
  Circle,
  Clock3,
  ExternalLink,
  MessageSquareText,
  ShieldCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  answerDemoAskLinkQuestion,
  type DemoAgentCitation,
  type DemoAgentExperience,
  type DemoAskLinkQuestion,
} from '@/lib/demo/demoAgenticExperience';

interface DemoAgenticAssistantProps {
  experience: DemoAgentExperience;
  className?: string;
}

const QUESTIONS: Array<{ id: DemoAskLinkQuestion; label: string }> = [
  { id: 'why_recommended', label: 'Why did LINK prepare this?' },
  { id: 'what_evidence', label: 'What evidence supports it?' },
  { id: 'what_is_missing', label: 'Does anything need confirmation?' },
  { id: 'what_happens_next', label: 'What happens next?' },
];

function sourceLabel(sourceType: DemoAgentCitation['sourceType']): string {
  if (sourceType === 'payer_policy') return 'Policy';
  if (sourceType === 'ncci_rule') return 'NCCI';
  if (sourceType === 'authorization') return 'Authorization';
  if (sourceType === 'payer_response') return 'Payer response';
  return 'Case evidence';
}

function phaseLabel(phase: DemoAgentExperience['phase']): string {
  if (phase === 'pre-auth') return 'Pre-Auth preparation';
  if (phase === 'post-op') return 'Post-Op reconciliation';
  return 'Denial response';
}

export function DemoAgenticAssistant({ experience, className }: DemoAgenticAssistantProps) {
  const [selectedQuestion, setSelectedQuestion] = useState<DemoAskLinkQuestion>('why_recommended');
  const reducedMotion = useReducedMotion();
  const answer = useMemo(
    () => answerDemoAskLinkQuestion(selectedQuestion, experience.askLinkContext),
    [experience.askLinkContext, selectedQuestion],
  );

  return (
    <section
      className={cn('overflow-hidden rounded-lg border border-sky-200 bg-sky-50/35', className)}
      aria-label="LINK supervised agent activity"
      data-demo-agentic-assistant={experience.phase}
    >
      <div className="flex flex-col gap-3 border-b border-sky-200/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-sky-200 bg-white text-primary">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-foreground">LINK Autopilot</p>
              <Badge variant="outline" className="border-sky-200 bg-white text-[10px] uppercase tracking-wide text-sky-800">
                Supervised agents
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {phaseLabel(experience.phase)} - synthetic evidence - human sign and send
            </p>
          </div>
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-2 bg-white">
              <MessageSquareText className="h-3.5 w-3.5" aria-hidden="true" />
              Ask LINK
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
            <SheetHeader className="pr-7">
              <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              </div>
              <SheetTitle>Ask LINK</SheetTitle>
              <SheetDescription>
                Evidence-linked explanations for this synthetic case. LINK cannot change clinical facts, sign, or send from this panel.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-5">
              <div className="grid gap-2" aria-label="Ask LINK questions">
                {QUESTIONS.map(question => (
                  <Button
                    key={question.id}
                    type="button"
                    variant={selectedQuestion === question.id ? 'secondary' : 'outline'}
                    className="h-auto min-h-10 justify-start whitespace-normal px-3 py-2 text-left"
                    onClick={() => setSelectedQuestion(question.id)}
                  >
                    {question.label}
                  </Button>
                ))}
              </div>

              <section aria-live="polite" className="border-l-2 border-primary pl-4">
                <p className="text-sm leading-6 text-foreground">{answer.answer}</p>
                <Badge variant="outline" className="mt-3 text-[10px] uppercase tracking-wide">
                  Explanation only
                </Badge>
              </section>

              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Cited sources
                </h3>
                {answer.citations.length > 0 ? (
                  <div className="divide-y divide-border rounded-lg border">
                    {answer.citations.map(citation => (
                      <div key={`${citation.sourceType}-${citation.id}`} className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{citation.label}</p>
                            {citation.excerpt && (
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">{citation.excerpt}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[9px] uppercase tracking-wide">
                            {sourceLabel(citation.sourceType)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No cited source is available for this explanation.</p>
                )}
              </section>

              <div className="flex items-start gap-2 border-t pt-4 text-xs leading-5 text-muted-foreground">
                <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>Production policy and evidence details remain governed by role and tenant access.</span>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className={cn(
        'grid gap-2 px-4 py-3 sm:grid-cols-2',
        experience.statusItems.length > 4 ? 'xl:grid-cols-5' : 'xl:grid-cols-4',
      )}>
        {experience.statusItems.map(item => (
          <div
            key={item.id}
            className="flex min-h-8 items-center gap-2 text-xs"
            data-agent-status={`${item.id}:${item.status}`}
          >
            {item.status === 'complete' ? (
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            ) : item.status === 'active' ? (
              <motion.span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700"
                animate={reducedMotion ? undefined : { opacity: [1, 0.55, 1] }}
                transition={reducedMotion ? undefined : { duration: 1.6, repeat: Infinity }}
              >
                <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
              </motion.span>
            ) : (
              <Circle className="h-5 w-5 shrink-0 text-muted-foreground/35" aria-hidden="true" />
            )}
            <span className={cn(
              'font-medium leading-5',
              item.status === 'complete' && 'text-emerald-800',
              item.status === 'active' && 'text-sky-800',
              item.status === 'pending' && 'text-muted-foreground',
            )}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
